import {DirectusService, DirectusTranslationPipe, HostingLocation} from '../directus';
import {inject, Injectable, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {catchError, map, of} from 'rxjs';
import {TranslocoService} from '@jsverse/transloco';

const REGION_ICONS: Record<string, string> = {
  ADEQ: 'pi-verified text-green',
  SCC: 'pi-shield text-blue',
  None: 'pi-exclamation-triangle text-yellow',
};

@Injectable({providedIn: 'root'})
export class HostingLocationService {
  private readonly directusService = inject(DirectusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly directusTranslationPipe = new DirectusTranslationPipe();

  readonly allHostingLocations = toSignal(
    this.directusService.loadHostingLocations().pipe(
      map((locations: HostingLocation[]) =>
        this._sortHostingLocationsByTypeAndName(locations, this.translocoService.getActiveLang()),
      ),
      catchError((err) => {
        console.error('Failed to load hosting locations from Directus:', err);
        return of([] as HostingLocation[]);
      }),
    ),
    {initialValue: [] as HostingLocation[]},
  );

  getHostingLocations(): Signal<HostingLocation[]> {
    return this.allHostingLocations;
  }

  getHostingLocationDisplayName(location: HostingLocation, lang?: string): string {
    const currentLang = lang ?? this.translocoService.getActiveLang();
    return (
      this.directusTranslationPipe.transform(location.translations, currentLang) ?? location.isoCode
    );
  }

  getHostingLocationFlagUrl(locationCode: string): string {
    return `https://flagcdn.com/w40/${locationCode.toLowerCase()}.png`;
  }

  getHostingLocationIconClass(location: HostingLocation): string {
    return REGION_ICONS[location.isoCode] ?? 'pi-globe';
  }

  private _sortHostingLocationsByTypeAndName(
    locations: HostingLocation[],
    lang: string,
  ): HostingLocation[] {
    return [...locations].sort((a, b) => {
      const typeOrder: Record<'region' | 'country', number> = {region: 0, country: 1};
      const typeComparison = typeOrder[a.type] - typeOrder[b.type];

      if (typeComparison !== 0) {
        return typeComparison;
      }

      const nameA = this.directusTranslationPipe.transform(a.translations, lang) || a.isoCode;
      const nameB = this.directusTranslationPipe.transform(b.translations, lang) || b.isoCode;
      return nameA.localeCompare(nameB);
    });
  }
}
